import { Link } from 'react-router';
import { BrandLockup } from './BrandMark';
import { useCases, guides, alternatives } from '../content';
import { competitors } from '../data/competitors';

interface FooterLink {
  label: string;
  to: string;
  external?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const productLinks: FooterLink[] = [
  { label: 'Features', to: '/#features' },
  { label: 'Feedback Pages', to: '/#feedback-pages' },
  { label: 'Developer API', to: '/developers' },
  { label: 'AI-native feedback', to: '/ai' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Download', to: '/download' },
  { label: 'Docs', to: '/docs' },
  { label: 'Changelog', to: '/changelog' },
];

const companyLinks: FooterLink[] = [
  { label: 'About', to: '/about' },
  { label: 'Compare', to: '/vs' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

const columns: FooterColumn[] = [
  { title: 'Product', links: productLinks },
  {
    title: 'Use cases',
    links: useCases.map((u) => ({ label: u.title, to: `/use-cases/${u.slug}` })),
  },
  {
    title: 'Guides',
    links: [
      ...guides.slice(0, 5).map((g) => ({ label: g.title.replace(/:.*$/, ''), to: `/guides/${g.slug}` })),
      ...alternatives.slice(0, 2).map((a) => ({ label: a.title.replace(/^The \d+ /, '').replace(/ in 2026$/, ''), to: `/alternatives/${a.slug}` })),
    ],
  },
  {
    title: 'Compare',
    links: competitors.map((c) => ({ label: `vs ${c.name}`, to: `/vs/${c.slug}` })),
  },
  { title: 'Company', links: companyLinks },
];

/**
 * Shared marketing footer. Surfaces the full internal-linking map (use cases,
 * guides, comparisons, alternatives) so both users and crawlers can reach every
 * cornerstone page — a key SEO crawl-depth and topical-authority signal.
 */
export function MarketingFooter() {
  return (
    <footer className="border-t border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2" aria-label="Pollenate home">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-lg font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <p className="mt-3 text-sm text-slate-500 dark:text-neutral-400">
              Embeddable customer feedback — widgets, surveys, NPS &amp; CSAT.
            </p>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-neutral-500">
                {col.title}
              </h2>
              <ul className="mt-3 space-y-2">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-slate-100 dark:border-neutral-900 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-slate-400 dark:text-neutral-500">
            &copy; {new Date().getFullYear()} Pollenate by{' '}
            <a
              href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              BlueHive Health
            </a>
            . All rights reserved.
          </p>
          <a
            href="https://x.com/PollenateCloud"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
          >
            @PollenateCloud
          </a>
        </div>
      </div>
    </footer>
  );
}
